import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import UserAvatar from "../components/UserAvatar";
import {
    ArrowLeft, Coins, Loader2, Plus, Users, Clock, AlertCircle, Check,
    X, TrendingUp, Handshake, Sparkles, Pencil, Ban, ThumbsUp, ThumbsDown, Timer,
} from "lucide-react";

function fmtRel(iso) {
    if (!iso) return "";
    const dt = new Date(iso).getTime();
    const diff = dt - Date.now();
    const abs = Math.abs(diff);
    const s = Math.round(abs / 1000);
    let out;
    if (s < 60) out = `${s}s`;
    else if (s < 3600) out = `${Math.round(s / 60)}m`;
    else if (s < 86400) out = `${Math.round(s / 3600)}h`;
    else out = `${Math.round(s / 86400)}d`;
    return diff > 0 ? `in ${out}` : `${out} ago`;
}

function CreateForm({ onCreated }) {
    const [desc, setDesc] = useState("");
    const [odds, setOdds] = useState("2");
    const [liability, setLiability] = useState(100);
    const [minutes, setMinutes] = useState(60);
    const [err, setErr] = useState("");
    const [busy, setBusy] = useState(false);
    const oddsF = parseFloat(odds) || 0;
    const implied = oddsF >= 1.01 ? Math.round(10000 / oddsF) / 100 : 0;
    const maxBuyerStake = oddsF > 1 ? Math.floor(liability / (oddsF - 1)) : 0;

    const submit = async () => {
        setErr("");
        if (desc.trim().length < 3) return setErr("Description too short");
        if (oddsF < 1.01) return setErr("Odds must be > 1.01");
        if (liability < 1) return setErr("Liability required");
        if (minutes < 1) return setErr("Timer must be at least 1 minute");
        setBusy(true);
        try {
            const deadline = new Date(Date.now() + minutes * 60 * 1000).toISOString();
            const { data } = await api.post("/bet-markets", {
                description: desc.trim(),
                odds: oddsF,
                total_liability: parseInt(liability),
                deadline,
            });
            setDesc(""); setOdds("2"); setLiability(100); setMinutes(60);
            onCreated && onCreated(data.market);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="bet-create-form" className="brutal-card p-4 bg-vibrantyellow">
            <div className="flex items-center gap-2 mb-3">
                <Plus className="w-5 h-5 text-ink" strokeWidth={3} />
                <h3 className="font-heading font-black text-lg text-ink tracking-tighter">Open a new bet</h3>
            </div>
            <textarea
                data-testid="bet-desc-input"
                rows={2}
                placeholder="What's the bet? e.g. 'BTC hits $200k this week'"
                value={desc}
                onChange={(e) => setDesc(e.target.value.slice(0, 280))}
                className="w-full bg-white border-[3px] border-ink rounded-xl p-2 font-body text-ink text-sm resize-none focus:outline-none"
            />
            <div className="grid grid-cols-3 gap-2 mt-2">
                <div>
                    <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Odds (payout ×)</label>
                    <input type="number" min={1.01} step={0.1} value={odds}
                        onChange={(e) => setOdds(e.target.value)}
                        data-testid="bet-odds-input"
                        className="w-full bg-white border-[3px] border-ink rounded-xl px-2 py-1.5 font-heading font-black text-lg text-ink focus:outline-none" />
                    {implied > 0 && <p className="font-body text-[10px] text-ink/60 mt-1">implied {implied}%</p>}
                </div>
                <div>
                    <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Your max loss (credits)</label>
                    <input type="number" min={1} value={liability}
                        onChange={(e) => setLiability(Math.max(1, parseInt(e.target.value) || 1))}
                        data-testid="bet-liability-input"
                        className="w-full bg-white border-[3px] border-ink rounded-xl px-2 py-1.5 font-heading font-black text-lg text-ink focus:outline-none" />
                </div>
                <div>
                    <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Timer (min)</label>
                    <input type="number" min={1} value={minutes}
                        onChange={(e) => setMinutes(Math.max(1, parseInt(e.target.value) || 1))}
                        data-testid="bet-timer-input"
                        className="w-full bg-white border-[3px] border-ink rounded-xl px-2 py-1.5 font-heading font-black text-lg text-ink focus:outline-none" />
                </div>
            </div>
            <p className="font-body text-xs text-ink/70 mt-2">
                Max stake buyers can risk: <b>{maxBuyerStake}</b> credits per ticket at current odds.
                Buyer wins <b>{Math.round(maxBuyerStake * oddsF)}</b> credits if they're right; you win the buyer's stake if they're wrong.
            </p>
            {err && (
                <div data-testid="bet-create-error" className="mt-2 flex items-start gap-2 bg-vibrantpink/25 border-[3px] border-vibrantpink text-ink rounded-xl p-2">
                    <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-vibrantpink" strokeWidth={2.5} />
                    <span className="font-body font-semibold text-xs">{err}</span>
                </div>
            )}
            <button type="button" onClick={submit} disabled={busy} data-testid="bet-create-submit" className="brutal-btn-primary mt-3 text-sm py-2 px-4 disabled:opacity-50">
                {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" strokeWidth={2.5} /> : <Plus className="w-4 h-4 mr-1.5" strokeWidth={2.5} />}
                Post bet · lock {liability}c
            </button>
        </div>
    );
}

function BuyTicketPanel({ market, onBought }) {
    const [stake, setStake] = useState(10);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const maxStake = market.odds > 1 ? Math.floor(market.liability_remaining / (market.odds - 1)) : 0;
    const potential = Math.round(stake * market.odds);

    const submit = async () => {
        setErr("");
        if (stake < 1) return setErr("Stake must be at least 1");
        setBusy(true);
        try {
            const { data } = await api.post(`/bet-markets/${market.id}/tickets`, { stake: parseInt(stake) });
            onBought && onBought(data.ticket);
            setStake(10);
        } catch (e) {
            setErr(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    return (
        <div className="border-t-[3px] border-ink/10 pt-3 mt-3">
            <div className="flex items-end gap-2">
                <div className="flex-1">
                    <label className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 block mb-1">Your stake · win {potential}c if YES</label>
                    <input type="number" min={1} max={maxStake} value={stake}
                        onChange={(e) => setStake(Math.min(maxStake, Math.max(1, parseInt(e.target.value) || 1)))}
                        data-testid={`bet-buy-stake-${market.id}`}
                        className="w-full bg-cream border-[3px] border-ink rounded-xl px-2 py-1.5 font-heading font-black text-lg text-ink focus:outline-none" />
                </div>
                <button type="button" onClick={submit} disabled={busy || stake > maxStake || stake < 1}
                    data-testid={`bet-buy-submit-${market.id}`}
                    className="brutal-btn-primary text-sm py-2 px-4 disabled:opacity-50">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2.5} /> : "Buy"}
                </button>
            </div>
            <p className="font-body text-[10px] text-ink/60 mt-1">Max stake: {maxStake}c (limited by seller's remaining liability)</p>
            {err && <p className="mt-1 font-body text-xs text-vibrantpink">{err}</p>}
        </div>
    );
}

function EditOddsPanel({ market, onSaved }) {
    const [odds, setOdds] = useState(market.odds);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    const save = async () => {
        setBusy(true);
        try {
            await api.patch(`/bet-markets/${market.id}/odds`, { odds: parseFloat(odds) });
            onSaved && onSaved();
            setOpen(false);
        } finally { setBusy(false); }
    };
    if (!open) return (
        <button type="button" onClick={() => setOpen(true)} data-testid={`bet-edit-odds-${market.id}`} className="brutal-btn-white text-xs py-1.5 px-2">
            <Pencil className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Edit odds
        </button>
    );
    return (
        <div className="flex items-center gap-1">
            <input type="number" min={1.01} step={0.1} value={odds}
                onChange={(e) => setOdds(e.target.value)}
                data-testid={`bet-edit-odds-input-${market.id}`}
                className="w-16 bg-white border-[3px] border-ink rounded-lg px-1.5 py-1 font-heading font-black text-sm focus:outline-none" />
            <button type="button" onClick={save} disabled={busy} data-testid={`bet-edit-odds-save-${market.id}`} className="brutal-btn bg-emerald-600 text-white text-xs py-1 px-2">
                <Check className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
            <button type="button" onClick={() => { setOdds(market.odds); setOpen(false); }} className="brutal-btn-white text-xs py-1 px-2">
                <X className="w-3.5 h-3.5" strokeWidth={3} />
            </button>
        </div>
    );
}

function TicketRow({ ticket, market, iAmCreator, iAmBuyer, onVoted }) {
    const [busy, setBusy] = useState(false);
    const vote = async (outcome) => {
        setBusy(true);
        try {
            await api.post(`/bet-markets/tickets/${ticket.id}/vote`, { outcome });
            onVoted && onVoted();
        } finally { setBusy(false); }
    };
    const settled = ticket.status.startsWith("SETTLED_");
    const canVote = market && new Date(market.deadline).getTime() <= Date.now() && !settled && (iAmCreator || iAmBuyer);
    const myVote = iAmCreator ? ticket.creator_vote : (iAmBuyer ? ticket.buyer_vote : null);
    return (
        <div data-testid={`bet-ticket-${ticket.id}`} className="brutal-card p-3 bg-cream flex flex-col sm:flex-row sm:items-center gap-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <UserAvatar username={ticket.buyer_username} size={22} className="!border-2 !shadow-none" />
                    <span className="font-heading font-bold text-sm text-ink">{ticket.buyer_username}</span>
                    <span className="font-body text-xs text-ink/50">staked {ticket.stake}c · win {ticket.potential_payout}c @ {ticket.odds_at_purchase}×</span>
                </div>
                <div className="font-body text-[10px] text-ink/60 mt-0.5">
                    status: <span className={`font-heading font-bold ${ticket.status === "SETTLED_YES" ? "text-emerald-600" : ticket.status === "SETTLED_NO" ? "text-vibrantpink" : ticket.status === "DISPUTED" ? "text-vibrantorange" : "text-ink"}`}>{ticket.status}</span>
                    {ticket.creator_vote && ` · creator: ${ticket.creator_vote}`}
                    {ticket.buyer_vote && ` · buyer: ${ticket.buyer_vote}`}
                </div>
            </div>
            {canVote && (
                <div className="flex gap-1">
                    <button type="button" onClick={() => vote("YES")} disabled={busy} data-testid={`bet-vote-yes-${ticket.id}`} className={`brutal-btn text-xs py-1.5 px-2 ${myVote === "YES" ? "bg-emerald-600 text-white" : "bg-white text-ink"}`}>
                        <ThumbsUp className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> YES
                    </button>
                    <button type="button" onClick={() => vote("NO")} disabled={busy} data-testid={`bet-vote-no-${ticket.id}`} className={`brutal-btn text-xs py-1.5 px-2 ${myVote === "NO" ? "bg-vibrantpink text-white" : "bg-white text-ink"}`}>
                        <ThumbsDown className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> NO
                    </button>
                </div>
            )}
        </div>
    );
}

function MarketCard({ market, tickets = [], currentUser, onChange }) {
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
    const iAmCreator = currentUser?.id === market.creator_id;
    const deadlineMs = new Date(market.deadline).getTime();
    const buyingOpen = market.status === "OPEN" && now < deadlineMs;
    const votingOpen = now >= deadlineMs;
    const myTicket = tickets.find((t) => t.buyer_id === currentUser?.id);

    return (
        <div data-testid={`bet-market-${market.id}`} className="brutal-card p-4 bg-white">
            <div className="flex items-start gap-2 mb-2">
                <UserAvatar username={market.creator_username} size={30} className="!border-2 !shadow-none" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <Link to={`/u/${market.creator_username}`} className="font-heading font-bold text-xs text-ink hover:text-vibrantorange">
                            {market.creator_username}
                        </Link>
                        <span className={`font-heading font-bold text-[10px] uppercase tracking-widest border-[2px] border-ink rounded px-1.5 ${market.status === "OPEN" ? "bg-vibrantyellow" : market.status === "CLOSED" ? "bg-cream" : "bg-emerald-600 text-white"}`}>{market.status}</span>
                    </div>
                    <p className="font-body text-sm text-ink mt-0.5 break-words">{market.description}</p>
                </div>
                {iAmCreator && market.status === "OPEN" && (
                    <EditOddsPanel market={market} onSaved={onChange} />
                )}
            </div>
            <div className="grid grid-cols-4 gap-2 mt-2">
                <div className="brutal-card p-2 bg-cream text-center">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Odds</div>
                    <div className="font-heading font-black text-lg text-ink" data-testid={`bet-odds-display-${market.id}`}>{market.odds}×</div>
                </div>
                <div className="brutal-card p-2 bg-cream text-center">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Left to sell</div>
                    <div className="font-heading font-black text-lg text-ink">{market.liability_remaining}c</div>
                </div>
                <div className="brutal-card p-2 bg-cream text-center">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Tickets</div>
                    <div className="font-heading font-black text-lg text-ink">{market.tickets_sold}</div>
                </div>
                <div className="brutal-card p-2 bg-cream text-center">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60">Timer</div>
                    <div className={`font-heading font-black text-lg ${buyingOpen ? "text-ink" : "text-vibrantpink"}`}>{fmtRel(market.deadline)}</div>
                </div>
            </div>

            {buyingOpen && !iAmCreator && !myTicket && currentUser && (
                <BuyTicketPanel market={market} onBought={onChange} />
            )}
            {myTicket && (
                <div className="mt-3 border-t-[3px] border-ink/10 pt-3">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 mb-1">Your ticket</div>
                    <TicketRow ticket={myTicket} market={market} iAmCreator={false} iAmBuyer={true} onVoted={onChange} />
                </div>
            )}
            {iAmCreator && tickets.length > 0 && (
                <div className="mt-3 border-t-[3px] border-ink/10 pt-3">
                    <div className="font-heading font-bold text-[10px] uppercase tracking-widest text-ink/60 mb-1">Buyers ({tickets.length})</div>
                    <div className="space-y-2">
                        {tickets.map((t) => (
                            <TicketRow key={t.id} ticket={t} market={market} iAmCreator={true} iAmBuyer={false} onVoted={onChange} />
                        ))}
                    </div>
                </div>
            )}
            {iAmCreator && market.status === "OPEN" && (
                <button type="button" onClick={async () => { if (!window.confirm("Withdraw remaining unmatched liability?")) return; await api.post(`/bet-markets/${market.id}/cancel`); onChange && onChange(); }}
                    data-testid={`bet-cancel-${market.id}`}
                    className="mt-3 brutal-btn-white text-xs py-1.5 px-3">
                    <Ban className="w-3.5 h-3.5 mr-1" strokeWidth={2.5} /> Withdraw unmatched
                </button>
            )}
        </div>
    );
}

export default function BetMarkets() {
    const { user } = useAuth();
    const [markets, setMarkets] = useState([]);
    const [ticketsByMarket, setTicketsByMarket] = useState({});
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("OPEN");
    const isLoggedIn = user && user !== false;

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/bet-markets?status=${filter}&limit=100`);
            setMarkets(data);
            // Fetch tickets for markets where I am creator or buyer
            const tbm = {};
            if (isLoggedIn) {
                for (const m of data) {
                    if (m.creator_id === user.id) {
                        const { data: detail } = await api.get(`/bet-markets/${m.id}`);
                        tbm[m.id] = detail.tickets;
                    }
                }
                // Also fetch my own tickets
                const { data: mine } = await api.get("/bet-markets/tickets/mine?limit=100");
                for (const row of mine) {
                    if (!tbm[row.ticket.market_id]) tbm[row.ticket.market_id] = [];
                    if (!tbm[row.ticket.market_id].find((t) => t.id === row.ticket.id)) {
                        tbm[row.ticket.market_id].push(row.ticket);
                    }
                }
            }
            setTicketsByMarket(tbm);
        } catch {}
        finally { setLoading(false); }
    }, [filter, isLoggedIn, user?.id]);

    useEffect(() => {
        load();
        const i = setInterval(load, 5000);
        return () => clearInterval(i);
    }, [load]);

    return (
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
            <Link to="/" data-testid="bet-back-link" className="inline-flex items-center gap-1.5 font-heading font-bold text-xs uppercase tracking-widest text-ink/60 hover:text-vibrantorange transition-colors mb-3">
                <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} /> Back
            </Link>

            <div className="flex gap-2 mb-4">
                <Link to="/bet-crypto" data-testid="bet-tab-crypto" className="brutal-btn bg-white text-ink text-xs py-2 px-3">
                    <TrendingUp className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> Crypto Prices
                </Link>
                <Link to="/bet-markets" data-testid="bet-tab-p2p" className="brutal-btn bg-vibrantorange text-white text-xs py-2 px-3">
                    <Handshake className="w-3.5 h-3.5 mr-1.5" strokeWidth={2.5} /> P2P Bets
                </Link>
            </div>

            <div className="flex items-start gap-3 mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 bg-vibrantorange border-[3px] border-ink rounded-2xl flex items-center justify-center shadow-brutal-lg flex-shrink-0">
                    <Handshake className="w-6 h-6 sm:w-7 sm:h-7 text-white" strokeWidth={2.5} />
                </div>
                <div className="flex-1">
                    <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink leading-tight">
                        Peer <span className="text-vibrantorange">Bets</span>
                    </h1>
                    <p className="font-body text-ink/70 mt-1">
                        Open your own bet, set your odds, and let others take the other side.
                    </p>
                </div>
            </div>

            {isLoggedIn ? (
                <div className="mb-4">
                    <CreateForm onCreated={load} />
                </div>
            ) : (
                <div data-testid="bet-login-gate" className="brutal-card p-4 bg-vibrantyellow mb-4">
                    <p className="font-heading font-bold text-sm text-ink">
                        <Link to="/login" className="text-vibrantorange hover:underline">Log in</Link> to open a bet or buy someone else's.
                    </p>
                </div>
            )}

            <div className="flex gap-2 mb-3" data-testid="bet-filter-tabs">
                {["OPEN", "CLOSED", "ALL"].map((f) => (
                    <button key={f} type="button" onClick={() => setFilter(f)} data-testid={`bet-filter-${f}`}
                        className={`brutal-btn text-xs py-1.5 px-3 ${filter === f ? "bg-ink text-white" : "bg-white text-ink"}`}>
                        {f}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="text-center py-10"><Loader2 className="w-6 h-6 mx-auto animate-spin text-vibrantorange" strokeWidth={2.5} /></div>
            ) : markets.length === 0 ? (
                <div data-testid="bet-empty" className="brutal-card p-6 bg-cream text-center">
                    <Sparkles className="w-8 h-8 mx-auto text-ink/40 mb-2" strokeWidth={2} />
                    <p className="font-heading font-bold text-ink/60">No bets right now — be the first to post one.</p>
                </div>
            ) : (
                <div className="space-y-3" data-testid="bet-market-list">
                    {markets.map((m) => (
                        <MarketCard
                            key={m.id}
                            market={m}
                            tickets={ticketsByMarket[m.id] || []}
                            currentUser={user}
                            onChange={load}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
